import React, { useState, useEffect, useCallback } from 'react';
import {
  UserGroupIcon,
  ChartBarIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ClockIcon,
  ChevronDownIcon,
  UsersIcon,
  SparklesIcon,
  TicketIcon,
  MegaphoneIcon,
  TrophyIcon,
  AcademicCapIcon,
  CurrencyEuroIcon,
  ExclamationCircleIcon,
  UserCircleIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  PhoneIcon,
  CakeIcon,
  HomeIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';

import LineChart from '../components/LineChart';

interface MemberGrowthItem {
  month: string;
  new_members: number;
  deleted_members: number;
  net_change: number;
  cumulative_total: number;
  active_members: number;
}

interface MemberGrowthData {
  all: MemberGrowthItem[];
  guests: MemberGrowthItem[];
  members: MemberGrowthItem[];
}

interface MemberActivity {
  month: string;
  active_members: number;
  total_bookings: number;
  unique_events: number;
  avg_bookings_per_member: number;
}

interface EventPopularity {
  unique_id: string;
  title: string;
  event_count: number;
  total_bookings: number;
  max_teilnehmer: number;
  avg_utilization: number;
  avg_bookings_per_event: number;
  trainer_name?: string;
  cotrainer_name?: string;
}

interface CurrentStats {
  total_members: number;
  new_members_year: number;
  left_members_year: number;
  active_members_year: number;
  bookings_year: number;
  total_events_year: number;
  unique_event_types_year: number;
  avg_bookings_per_member_year: number;
}

interface ProfileCompleteness {
  overall?: {
    total_members: number;
    avg_completeness: number;
    with_photo: number;
    with_email: number;
    with_phone: number;
    with_birthdate: number;
    with_address: number;
    fully_complete: number;
    missing_critical: number;
  };
  distribution?: Array<{
    category: string;
    count: number;
    percentage: number;
    avg_completeness: number;
  }>;
  top_incomplete?: Array<{
    name: string;
    completeness: number;
    missing_fields: number;
    has_photo: number;
    has_email: number;
    has_phone: number;
  }>;
}

interface TopTrainer {
  trainer_name: string;
  total_events: number;
  unique_event_types: number;
  total_bookings: number;
  unique_participants: number;
  avg_utilization: number;
  avg_bookings_per_event: number;
  success_rate: number;
  event_types: string;
}

interface TopMember {
  member_name: string;
  total_bookings: number;
  unique_event_types: number;
  active_days: number;
  active_months: number;
  first_booking: string;
  last_booking: string;
  event_types: string;
  participation_rate: number;
  avg_bookings_per_month: number;
}

interface GuestCardStats {
  revenue: {
    total: number;
    paid: number;
    unpaid: number;
  };
  cards: {
    total: number;
    active: number;
    depleted: number;
    soldThisMonth: number;
    soldLastMonth: number;
  };
  bookings: {
    total: number;
    thisMonth: number;
    lastMonth: number;
    uniqueEvents: number;
  };
  averages: {
    entriesPerCard: number;
    pricePerCard: number;
  };
  typeDistribution: Array<{
    name: string;
    count: number;
    revenue: number;
    avgRemaining: number;
  }>;
  monthlyRevenue: Array<{
    month: string;
    revenue: number;
    cardsSold: number;
    uniqueBuyers: number;
  }>;
}

// Tab-specific data cache interface
interface TabDataCache {
  memberGrowth?: MemberGrowthData;
  memberActivity?: MemberActivity[];
  eventPopularity?: EventPopularity[];
  activityTrends?: any;
  topEvents?: any[];
  profileCompleteness?: ProfileCompleteness;
  topTrainers?: TopTrainer[];
  topMembers?: TopMember[];
  guestCardStats?: GuestCardStats;
}

// Tab loading states
interface TabLoadingStates {
  growth: boolean;
  activity: boolean;
  events: boolean;
  profiles: boolean;
  trainers: boolean;
  members: boolean;
  guestcards: boolean;
}

const MemberStatistics: React.FC = () => {
  // Current stats (always loaded initially)
  const [currentStats, setCurrentStats] = useState<CurrentStats | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState<'growth' | 'activity' | 'events' | 'profiles' | 'trainers' | 'members' | 'guestcards'>('growth');

  // Tab-specific data cache
  const [tabDataCache, setTabDataCache] = useState<TabDataCache>({});
  const [tabLoadingStates, setTabLoadingStates] = useState<TabLoadingStates>({
    growth: false,
    activity: false,
    events: false,
    profiles: false,
    trainers: false,
    members: false,
    guestcards: false
  });

  // Load initial overview stats on mount
  useEffect(() => {
    fetchOverviewStats();
  }, []);

  // Load tab data when tab changes or year changes
  useEffect(() => {
    // Check if we already have data for this tab and year
    const hasData = checkTabDataExists(activeTab);
    if (!hasData && !tabLoadingStates[activeTab]) {
      fetchTabData(activeTab);
    }
  }, [activeTab, selectedYear]);

  // Clear cache when year changes (except growth which is year-independent)
  useEffect(() => {
    setTabDataCache(prev => ({
      memberGrowth: prev.memberGrowth // Keep growth data as it's for all years
    }));
    setTabLoadingStates({
      growth: false,
      activity: false,
      events: false,
      profiles: false,
      trainers: false,
      members: false,
      guestcards: false
    });
    fetchOverviewStats(); // Reload overview stats for new year
  }, [selectedYear]);

  const checkTabDataExists = (tab: string): boolean => {
    switch (tab) {
      case 'growth':
        return !!tabDataCache.memberGrowth;
      case 'activity':
        return !!tabDataCache.memberActivity && !!tabDataCache.activityTrends;
      case 'events':
        return !!tabDataCache.eventPopularity && !!tabDataCache.topEvents;
      case 'profiles':
        return !!tabDataCache.profileCompleteness;
      case 'trainers':
        return !!tabDataCache.topTrainers;
      case 'members':
        return !!tabDataCache.topMembers;
      case 'guestcards':
        return !!tabDataCache.guestCardStats;
      default:
        return false;
    }
  };

  const fetchOverviewStats = async () => {
    try {
      setInitialLoading(true);
      const response = await apiClient.get(`/api/member-statistics/stats/overview?year=${selectedYear}`);
      if (response.data.success) {
        setCurrentStats(response.data.data.currentStats);
      }
    } catch (err) {
      console.error('Error fetching overview statistics:', err);
      setError('Fehler beim Laden der Übersichtsstatistiken');
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchTabData = async (tab: string) => {
    // Set loading state for this tab
    setTabLoadingStates(prev => ({ ...prev, [tab]: true }));

    try {
      let endpoint = '';
      let processResponse: (data: any) => void = () => {};

      switch (tab) {
        case 'growth':
          endpoint = `/api/member-statistics/stats/member-growth-all`;
          processResponse = (data) => {
            setTabDataCache(prev => ({ ...prev, memberGrowth: data.memberGrowth }));
          };
          break;

        case 'activity':
          // Load both member activity and activity trends for this tab
          const [activityRes, trendsRes] = await Promise.all([
            apiClient.get(`/api/member-statistics/stats/member-activity?year=${selectedYear}`),
            apiClient.get(`/api/member-statistics/stats/activity-trends?year=${selectedYear}`)
          ]);

          if (activityRes.data.success && trendsRes.data.success) {
            setTabDataCache(prev => ({
              ...prev,
              memberActivity: activityRes.data.data.memberActivity,
              activityTrends: trendsRes.data.data.activityTrends
            }));
          }
          setTabLoadingStates(prev => ({ ...prev, [tab]: false }));
          return; // Exit early since we handled this case specially

        case 'events':
          endpoint = `/api/member-statistics/stats/event-popularity?year=${selectedYear}`;
          processResponse = (data) => {
            setTabDataCache(prev => ({
              ...prev,
              eventPopularity: data.eventPopularity,
              topEvents: data.topEvents
            }));
          };
          break;

        case 'profiles':
          endpoint = '/api/member-statistics/stats/profiles';
          processResponse = (data) => {
            setTabDataCache(prev => ({ ...prev, profileCompleteness: data.profileCompleteness }));
          };
          break;

        case 'trainers':
          endpoint = `/api/member-statistics/stats/trainers?year=${selectedYear}`;
          processResponse = (data) => {
            setTabDataCache(prev => ({ ...prev, topTrainers: data.topTrainers }));
          };
          break;

        case 'members':
          endpoint = `/api/member-statistics/stats/top-members?year=${selectedYear}`;
          processResponse = (data) => {
            setTabDataCache(prev => ({ ...prev, topMembers: data.topMembers }));
          };
          break;

        case 'guestcards':
          endpoint = `/api/member-statistics/stats/guest-cards?year=${selectedYear}`;
          processResponse = (data) => {
            setTabDataCache(prev => ({ ...prev, guestCardStats: data.guestCardStats }));
          };
          break;

        default:
          setTabLoadingStates(prev => ({ ...prev, [tab]: false }));
          return;
      }

      const response = await apiClient.get(endpoint);
      if (response.data.success) {
        processResponse(response.data.data);
      }
    } catch (err) {
      console.error(`Error fetching ${tab} data:`, err);
      setError(`Fehler beim Laden der ${tab} Daten`);
    } finally {
      setTabLoadingStates(prev => ({ ...prev, [tab]: false }));
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // State for member type selection
  const [memberType, setMemberType] = React.useState<'all' | 'guests' | 'members'>('all');

  // Prepare data for LineChart component - all years
  const prepareMemberGrowthData = (type: 'all' | 'guests' | 'members' = memberType) => {
    if (!tabDataCache.memberGrowth || !tabDataCache.memberGrowth[type]) return [];

    return tabDataCache.memberGrowth[type].map((item: MemberGrowthItem) => ({
      date: item.month + '-01',
      purchases: {
        count: Number(item.new_members),
        revenue: 0,
        unique_buyers: 0
      },
      payments: {
        count: Number(item.deleted_members),
        revenue: 0,
        avg_delay: null
      },
      bookings: {
        count: Number(item.active_members),
        unique_users: 0,
        unique_events: 0
      },
      cumulative: {
        purchases: Number(item.cumulative_total),
        revenue: 0,
        payments: 0,
        bookings: Number(item.active_members)
      }
    }));
  };

  // Prepare data filtered by selected year
  const prepareMemberGrowthDataForYear = (type: 'all' | 'guests' | 'members' = memberType) => {
    if (!tabDataCache.memberGrowth || !tabDataCache.memberGrowth[type]) return [];

    const filtered = tabDataCache.memberGrowth[type].filter((item: MemberGrowthItem) =>
      item.month.startsWith(selectedYear.toString())
    );

    return filtered.map((item: MemberGrowthItem) => ({
      date: item.month + '-01',
      purchases: {
        count: Number(item.new_members),
        revenue: 0,
        unique_buyers: 0
      },
      payments: {
        count: Number(item.deleted_members),
        revenue: 0,
        avg_delay: null
      },
      bookings: {
        count: Number(item.active_members),
        unique_users: 0,
        unique_events: 0
      },
      cumulative: {
        purchases: Number(item.cumulative_total),
        revenue: 0,
        payments: 0,
        bookings: Number(item.active_members)
      }
    }));
  };

  const prepareActivityData = () => {
    if (!tabDataCache.memberActivity) return [];

    return tabDataCache.memberActivity.map(item => ({
      date: item.month + '-01',
      purchases: {
        count: item.active_members,
        revenue: 0,
        unique_buyers: 0
      },
      payments: {
        count: 0,
        revenue: 0,
        avg_delay: null
      },
      bookings: {
        count: item.total_bookings,
        unique_users: item.active_members,
        unique_events: item.unique_events
      },
      cumulative: {
        purchases: 0,
        revenue: 0,
        payments: 0,
        bookings: item.total_bookings
      }
    }));
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen font-sans text-gray-800 dark:text-slate-200" style={{ background: 'transparent' }}>
        <div className="max-w-7xl mx-auto py-2 sm:py-4 flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  if (error && !currentStats) {
    return (
      <div className="min-h-screen font-sans text-gray-800 dark:text-slate-200" style={{ background: 'transparent' }}>
        <div className="max-w-7xl mx-auto py-2 sm:py-4">
          <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432]" style={{ border: '1px solid rgba(244,63,94,0.30)' }}>
            <p className="text-rose-800 dark:text-rose-200">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const monthYear = new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  const newMembersYear = currentStats?.new_members_year || 0;
  const totalMembers = currentStats?.total_members || 0;
  const activeYear = currentStats?.active_members_year || 0;
  const totalEvents = currentStats?.total_events_year || 0;
  const totalBookings = currentStats?.bookings_year || 0;

  const tabs: Array<{ id: typeof activeTab; label: string }> = [
    { id: 'growth', label: 'Mitgliederentwicklung' },
    { id: 'activity', label: 'Aktivität' },
    { id: 'events', label: 'Events' },
    { id: 'profiles', label: 'Profilvollständigkeit' },
    { id: 'trainers', label: 'Trainer' },
    { id: 'members', label: 'Top Mitglieder' },
    { id: 'guestcards', label: 'Gästekarten' },
  ];

  return (
    <div className="min-h-screen font-sans text-gray-800 dark:text-slate-200" style={{ background: 'transparent' }}>
      <div className="max-w-7xl mx-auto py-2 sm:py-4">

        {/* INTRO Schriftzug */}
        <section className="pt-2 pb-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-cyan-600/80 dark:text-cyan-400/80 font-semibold mb-3">
            Auswertung · Stand {monthYear}
          </div>
          <div className="flex items-start justify-between gap-4">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight" style={{ letterSpacing: '-0.04em' }}>
              Vereins-<br />
              <span className="text-cyan-600 dark:text-cyan-400">Zahlen.</span>
            </h1>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-24 h-11 px-3 rounded-xl text-gray-900 dark:text-white text-sm font-bold bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
            >
              {Array.from({ length: new Date().getFullYear() - 2021 }, (_, i) => 2022 + i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mt-4">
            {currentStats ? (
              <>
                <span className="font-bold text-gray-900 dark:text-white">{totalMembers} Mitglieder</span>
                {newMembersYear !== 0 && (
                  <> · <span className={`font-bold ${newMembersYear > 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>{newMembersYear > 0 ? '+' : ''}{newMembersYear} im Jahr {selectedYear}</span></>
                )}
                {activeYear > 0 && <> · <span className="font-bold text-gray-900 dark:text-white">{activeYear} aktiv</span></>}
                {totalEvents > 0 && <> · <span className="font-bold text-cyan-700 dark:text-cyan-300">{totalEvents} Events</span></>}
                {totalBookings > 0 && <> mit <span className="font-bold text-gray-900 dark:text-white">{totalBookings} Buchungen</span></>}
                .
              </>
            ) : (
              <>Lade Vereins-Kennzahlen für <span className="font-bold text-gray-900 dark:text-white">{selectedYear}</span> …</>
            )}
          </p>
        </section>

        {/* Tab Navigation - Scrollable */}
        <div className="mb-6 overflow-x-auto border-b border-gray-200 dark:border-slate-700">
          <div className="flex gap-2 min-w-max">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2 font-bold text-sm transition-colors whitespace-nowrap ${
                  activeTab === t.id
                    ? 'text-primary-700 dark:text-primary-400 border-b-2 border-primary-500'
                    : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Loading indicator for tab content */}
        {tabLoadingStates[activeTab] && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        )}

      {/* Content based on active tab */}
      {activeTab === 'growth' && !tabLoadingStates.growth && (
        <div className="space-y-6">
          {/* Description */}
          <div className="rounded-2xl p-4 bg-blue-500/10 border border-blue-500/30">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Mitgliederentwicklung:</strong> Zeigt die Zu- und Abgänge von Vereinsmitgliedern über die gesamte Vereinsgeschichte
              und detailliert für das ausgewählte Jahr {selectedYear}.
            </p>
          </div>

          {/* Member Type Selector */}
          <div className="rounded-3xl p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
            <div className="flex gap-2">
              <button
                onClick={() => setMemberType('all')}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  memberType === 'all'
                    ? 'bg-primary-500 text-slate-950'
                    : 'bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/10'
                }`}
              >
                Alle Mitglieder
              </button>
              <button
                onClick={() => setMemberType('members')}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  memberType === 'members'
                    ? 'bg-primary-500 text-slate-950'
                    : 'bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/10'
                }`}
              >
                Vollmitglieder
              </button>
              <button
                onClick={() => setMemberType('guests')}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  memberType === 'guests'
                    ? 'bg-primary-500 text-slate-950'
                    : 'bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/10'
                }`}
              >
                Gäste
              </button>
            </div>
          </div>

          {/* Chart: Entire club history */}
          {tabDataCache.memberGrowth && tabDataCache.memberGrowth[memberType] && tabDataCache.memberGrowth[memberType].length > 0 && (
            <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Gesamte Vereinsgeschichte - {memberType === 'all' ? 'Alle' : memberType === 'guests' ? 'Gäste' : 'Vollmitglieder'}
                </h3>
                <UserGroupIcon className="h-5 w-5 text-info-500" />
              </div>
              <LineChart
                data={prepareMemberGrowthData()}
                type="activity"
                labels={{
                  purchases: 'Neue Mitglieder',
                  bookings: 'Aktive Mitglieder'
                }}
              />
              <div className="mt-4 flex justify-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-info-500 rounded-full"></div>
                  <span className="text-sm text-gray-600 dark:text-slate-400">Neue Mitglieder</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
                  <span className="text-sm text-gray-600 dark:text-slate-400">Aktive Mitglieder</span>
                </div>
              </div>
            </div>
          )}

          {/* Chart: Selected year detail */}
          {tabDataCache.memberGrowth && tabDataCache.memberGrowth[memberType] && tabDataCache.memberGrowth[memberType].filter((item: MemberGrowthItem) => item.month.startsWith(selectedYear.toString())).length > 0 ? (
            <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Detailansicht {selectedYear} - {memberType === 'all' ? 'Alle' : memberType === 'guests' ? 'Gäste' : 'Vollmitglieder'}
                </h3>
                <CalendarIcon className="h-5 w-5 text-success-500" />
              </div>
              <LineChart
                data={prepareMemberGrowthDataForYear()}
                type="activity"
                labels={{
                  purchases: 'Neue Mitglieder',
                  bookings: 'Aktive Mitglieder'
                }}
              />
              <div className="mt-4 flex justify-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-info-500 rounded-full"></div>
                  <span className="text-sm text-gray-600 dark:text-slate-400">Neue Mitglieder</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
                  <span className="text-sm text-gray-600 dark:text-slate-400">Aktive Mitglieder</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-3xl p-8 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <CalendarIcon className="h-12 w-12 text-gray-500 dark:text-slate-500 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-slate-400">
                Keine Daten für das Jahr {selectedYear} verfügbar.
              </p>
            </div>
          )}

          {/* Member Growth Table - only selected year */}
          {tabDataCache.memberGrowth && tabDataCache.memberGrowth[memberType] && tabDataCache.memberGrowth[memberType].filter((item: MemberGrowthItem) => item.month.startsWith(selectedYear.toString())).length > 0 ? (
            <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Monatliche Übersicht {selectedYear} - {memberType === 'all' ? 'Alle' : memberType === 'guests' ? 'Gäste' : 'Vollmitglieder'}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 px-4 text-sm font-medium text-gray-700 dark:text-slate-300">Monat</th>
                      <th className="text-right py-2 px-4 text-sm font-medium text-gray-700 dark:text-slate-300">Neu</th>
                      <th className="text-right py-2 px-4 text-sm font-medium text-gray-700 dark:text-slate-300">Ausgetreten</th>
                      <th className="text-right py-2 px-4 text-sm font-medium text-gray-700 dark:text-slate-300">Netto</th>
                      <th className="text-right py-2 px-4 text-sm font-medium text-gray-700 dark:text-slate-300">Gesamt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tabDataCache.memberGrowth[memberType]
                      .filter((item: MemberGrowthItem) => item.month.startsWith(selectedYear.toString()))
                      .map((item: MemberGrowthItem, index: number) => (
                        <tr key={index} className="border-b border-slate-700/50">
                          <td className="py-2 px-4 text-sm text-gray-900 dark:text-white">
                            {new Date(item.month + '-01').toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
                          </td>
                          <td className="py-2 px-4 text-sm text-right">
                            <span className="text-emerald-600 dark:text-emerald-400">+{Number(item.new_members)}</span>
                          </td>
                          <td className="py-2 px-4 text-sm text-right">
                            <span className="text-rose-600 dark:text-rose-400">-{Number(item.deleted_members)}</span>
                          </td>
                          <td className="py-2 px-4 text-sm text-right font-medium">
                            <span className={Number(item.net_change) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}>
                              {Number(item.net_change) >= 0 ? '+' : ''}{Number(item.net_change)}
                            </span>
                          </td>
                          <td className="py-2 px-4 text-sm text-right font-bold text-gray-900 dark:text-white">
                            {item.active_members}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="rounded-3xl p-8 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <ExclamationCircleIcon className="h-12 w-12 text-gray-500 dark:text-slate-500 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-slate-400">Keine Daten für die Mitgliederentwicklung verfügbar.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'activity' && !tabLoadingStates.activity && (
        <div className="space-y-6">
          {/* Description */}
          <div className="rounded-2xl p-4 bg-emerald-500/10 border border-emerald-500/30">
            <p className="text-sm text-emerald-800 dark:text-emerald-200">
              <strong>Aktivitätsanalyse:</strong> Zeigt die Buchungsaktivität der Mitglieder, Wochentagsverteilung
              und zeitliche Muster. Erkennen Sie Spitzenzeiten und optimieren Sie Ihr Kursangebot.
            </p>
          </div>

          {tabDataCache.memberActivity && tabDataCache.memberActivity.length > 0 && (
            <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Aktivitätsverlauf</h3>
                <SparklesIcon className="h-5 w-5 text-primary-500" />
              </div>
              <LineChart data={prepareActivityData()} type="activity" />
            </div>
          )}

          {/* Weekday Distribution */}
          {tabDataCache.activityTrends?.weekday_distribution ? (
            <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Wochentagsverteilung</h3>
                <ClockIcon className="h-5 w-5 text-primary-500" />
              </div>
              <p className="text-sm text-gray-600 dark:text-slate-400 mb-6">
                Verteilung aller angebotenen Veranstaltungen und deren Buchungen im Jahr {selectedYear}.
                Zeigt <strong>ALLE Events</strong> (auch einmalige und ungebuchte) mit der <strong>Gesamtzahl der Buchungen</strong> und dem <strong>Durchschnitt pro Veranstaltungstag</strong>.
              </p>
              <div className="space-y-4">
                {(() => {
                  // Erstelle ein vollständiges Array mit allen Wochentagen
                  const weekDays = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
                  const weekdayDist = tabDataCache.activityTrends?.weekday_distribution || [];
                  const maxBookings = weekdayDist.length > 0 ? Math.max(...weekdayDist.map((d: any) => d.bookings), 1) : 1;

                  return weekDays.map((dayName) => {
                    const dayData = weekdayDist.find((d: any) => d.day === dayName);
                    const bookings = dayData?.bookings || 0;
                    const avgBookings = dayData?.avg_bookings || 0;
                    const activeDays = dayData?.active_days || 0;
                    const percentage = bookings > 0 ? (bookings / maxBookings) * 100 : 0;

                    return (
                      <div key={dayName}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-700 dark:text-slate-300 w-24">
                              {dayName}
                            </span>
                            {activeDays > 0 ? (
                              <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-slate-400">
                                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                  {bookings} Buchungen
                                </span>
                                <span className="text-xs">
                                  Ø {avgBookings}/Tag • {activeDays} Event{activeDays !== 1 ? 's' : ''}
                                </span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500 dark:text-slate-500 italic">
                                Keine Veranstaltungen
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="w-full h-8 rounded-lg bg-white/5 border border-white/10 relative overflow-hidden">
                          {activeDays > 0 && (
                            <div
                              className="absolute left-0 top-0 h-full bg-cyan-500 transition-all duration-500 ease-out"
                              style={{ width: `${percentage}%` }}
                            >
                              <div className="absolute inset-0 bg-white/10"></div>
                            </div>
                          )}
                          {activeDays > 0 && percentage > 0 && (
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-xs font-medium text-gray-900 dark:text-white z-10">
                              {Math.round(percentage)}%
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
              <div className="mt-6 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                <p className="text-xs text-emerald-800 dark:text-emerald-200">
                  <strong>Hinweis:</strong> Diese Statistik erfasst <strong>ALLE angebotenen Events</strong>, einschließlich einmaliger Veranstaltungen und Events ohne Buchungen.
                  Die Prozentzahl zeigt das Verhältnis zu dem Wochentag mit den meisten Buchungen.
                </p>
              </div>
            </div>
          ) : (
            <div className="rounded-3xl p-8 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <ExclamationCircleIcon className="h-12 w-12 text-gray-500 dark:text-slate-500 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-slate-400">Keine Aktivitätsdaten verfügbar.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'events' && !tabLoadingStates.events && (
        <div className="space-y-6">
          {/* Description */}
          <div className="rounded-2xl p-4 bg-amber-500/10 border border-amber-500/30">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Event-Analyse:</strong> Die beliebtesten Veranstaltungen nach Buchungen und Auslastung.
              Sehen Sie, welche Events gut besucht sind und wer sie leitet. Nutzen Sie diese Daten zur Kursplanung.
            </p>
          </div>

          {/* Popular Events */}
          {tabDataCache.eventPopularity && tabDataCache.eventPopularity.length > 0 && (
            <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Beliebte Events (Jahresübersicht)</h3>
                <CalendarIcon className="h-5 w-5 text-primary-500" />
              </div>
              <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">
                Events gruppiert nach Typ (unique_id). Die <strong>Auslastung</strong> zeigt den durchschnittlichen Prozentsatz
                der belegten Plätze im Verhältnis zur maximalen Teilnehmerzahl über alle Termine dieses Event-Typs.
              </p>
              <div className="space-y-3">
                {tabDataCache.eventPopularity.slice(0, 10).map((event, index) => (
                  <div key={index} className="p-4 rounded-xl bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-gray-900 dark:text-white">{event.title}</p>
                      <div className="text-right">
                        <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                          {event.avg_utilization != null
                            ? `${event.avg_utilization}%`
                            : event.max_teilnehmer > 0
                              ? `${Math.round((event.avg_bookings_per_event / event.max_teilnehmer) * 100)}%`
                              : 'N/A'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-500">Ø Auslastung</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="h-3 w-3 text-gray-500 dark:text-slate-500" />
                        <span className="text-gray-600 dark:text-slate-400">
                          {event.event_count} Termine angeboten
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <UsersIcon className="h-3 w-3 text-gray-500 dark:text-slate-500" />
                        <span className="text-gray-600 dark:text-slate-400">
                          Max. {event.max_teilnehmer} Plätze/Termin
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <TicketIcon className="h-3 w-3 text-gray-500 dark:text-slate-500" />
                        <span className="text-gray-600 dark:text-slate-400">
                          {event.total_bookings} Buchungen gesamt
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <ChartBarIcon className="h-3 w-3 text-gray-500 dark:text-slate-500" />
                        <span className="text-gray-600 dark:text-slate-400">
                          Ø {event.avg_bookings_per_event} Buchungen/Termin
                        </span>
                      </div>
                    </div>
                    {(event.trainer_name || event.cotrainer_name) && (
                      <p className="text-xs text-gray-500 dark:text-slate-500 mt-2 pt-2 border-t border-slate-700">
                        <strong>Leitung:</strong> {event.trainer_name}
                        {event.cotrainer_name && ` • Co-Trainer: ${event.cotrainer_name}`}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 rounded-xl bg-blue-500/10 border border-blue-500/30">
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  <strong>Beispiel:</strong> "42 Termine · 175 Buchungen · 72% Auslastung" bedeutet:
                  Dieses Event wurde 42 mal angeboten, hatte insgesamt 175 Teilnehmer-Buchungen und war im Durchschnitt zu 72% ausgelastet
                  (z.B. bei max. 6 Plätzen waren durchschnittlich 4.3 Plätze belegt).
                </p>
              </div>
            </div>
          )}

          {/* No Event Data */}
          {(!tabDataCache.eventPopularity || tabDataCache.eventPopularity.length === 0) && (
            <div className="rounded-3xl p-8 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <ExclamationCircleIcon className="h-12 w-12 text-gray-500 dark:text-slate-500 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-slate-400">Keine Event-Daten verfügbar.</p>
            </div>
          )}

          {/* Top Events by Month */}
          {tabDataCache.topEvents && tabDataCache.topEvents.length > 0 && (
            <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Top Einzeltermine nach Buchungen</h3>
                <ArrowTrendingUpIcon className="h-5 w-5 text-success-500" />
              </div>
              <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">
                Die einzelnen Termine mit den meisten Buchungen. Diese Übersicht zeigt <strong>konkrete Einzelveranstaltungen</strong>,
                nicht Event-Typen. Die Prozentzahl zeigt die Auslastung dieses spezifischen Termins.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {tabDataCache.topEvents.slice(0, 6).map((event: any, index) => (
                  <div key={index} className="p-4 rounded-xl bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{event.title}</span>
                      <span className="text-xs px-2 py-1 rounded-full bg-white/10">
                        {new Date(event.month + '-01').toLocaleDateString('de-DE', { month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    {(event.trainer_name || event.cotrainer_name) && (
                      <p className="text-xs text-gray-500 dark:text-slate-500 mb-3">
                        <strong>Leitung:</strong> {event.trainer_name}
                        {event.cotrainer_name && ` • ${event.cotrainer_name}`}
                      </p>
                    )}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500 dark:text-slate-500">Buchungen</span>
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                          {event.bookings} von max. {event.max_teilnehmer}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500 dark:text-slate-500">Auslastung</span>
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                          {event.utilization_percent}%
                        </span>
                      </div>
                      <div className="flex flex-col col-span-2">
                        <span className="text-xs text-gray-500 dark:text-slate-500">Unique Teilnehmer</span>
                        <span className="font-semibold text-gray-700 dark:text-slate-300">
                          {event.unique_members} verschiedene Personen
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'profiles' && !tabLoadingStates.profiles && (
        <div className="space-y-6">
          {/* Description */}
          <div className="rounded-2xl p-4 bg-emerald-500/10 border border-emerald-500/30">
            <p className="text-sm text-emerald-800 dark:text-emerald-200">
              <strong>Datenqualität:</strong> Übersicht über die Vollständigkeit der Mitgliederprofile.
              Vollständige Profile ermöglichen bessere Kommunikation und Verwaltung. Identifizieren Sie Profile, die Aufmerksamkeit benötigen.
            </p>
          </div>

          {/* Overall Profile Completeness Stats */}
          {tabDataCache.profileCompleteness && tabDataCache.profileCompleteness.overall && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {/* Average Completeness */}
              <div className="rounded-2xl p-3 sm:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
                <div className="flex flex-col">
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400 mb-1">Ø Vollständigkeit</p>
                  <div className="flex items-center justify-between">
                    <p className="text-lg sm:text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                      {tabDataCache.profileCompleteness.overall.avg_completeness}%
                    </p>
                    <ChartBarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-info-500 flex-shrink-0" />
                  </div>
                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-500 mt-1">
                    {tabDataCache.profileCompleteness.overall.fully_complete} vollständig
                  </p>
                </div>
              </div>

              {/* With Photo */}
              <div className="rounded-2xl p-3 sm:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
                <div className="flex flex-col">
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400 mb-1">Mit Foto</p>
                  <div className="flex items-center justify-between">
                    <p className="text-lg sm:text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                      {tabDataCache.profileCompleteness.overall.with_photo}
                    </p>
                    <UserCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-success-500 flex-shrink-0" />
                  </div>
                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-500 mt-1">
                    {Math.round(tabDataCache.profileCompleteness.overall.with_photo / tabDataCache.profileCompleteness.overall.total_members * 100)}%
                  </p>
                </div>
              </div>

              {/* With Email */}
              <div className="rounded-2xl p-3 sm:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
                <div className="flex flex-col">
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400 mb-1">Mit E-Mail</p>
                  <div className="flex items-center justify-between">
                    <p className="text-lg sm:text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                      {tabDataCache.profileCompleteness.overall.with_email}
                    </p>
                    <EnvelopeIcon className="h-5 w-5 sm:h-6 sm:w-6 text-primary-500 flex-shrink-0" />
                  </div>
                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-500 mt-1">
                    {Math.round(tabDataCache.profileCompleteness.overall.with_email / tabDataCache.profileCompleteness.overall.total_members * 100)}%
                  </p>
                </div>
              </div>

              {/* Missing Critical */}
              <div className="rounded-2xl p-3 sm:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
                <div className="flex flex-col">
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400 mb-1">Kritisch</p>
                  <div className="flex items-center justify-between">
                    <p className="text-lg sm:text-2xl font-bold text-rose-600 dark:text-rose-400">
                      {tabDataCache.profileCompleteness.overall.missing_critical}
                    </p>
                    <ExclamationTriangleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-error-500 flex-shrink-0" />
                  </div>
                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-500 mt-1">
                    ohne E-Mail/Tel
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Distribution Chart */}
          {tabDataCache.profileCompleteness && tabDataCache.profileCompleteness.distribution && tabDataCache.profileCompleteness.distribution.length > 0 && (
            <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Vollständigkeitsverteilung</h3>
              <div className="space-y-3">
                {tabDataCache.profileCompleteness.distribution.map((item) => {
                  const colorClass = item.category.includes('100%') ? 'bg-success-500' :
                                    item.category.includes('80-99%') ? 'bg-info-500' :
                                    item.category.includes('60-79%') ? 'bg-warning-500' :
                                    item.category.includes('40-59%') ? 'bg-warning-500' : 'bg-error-500';

                  return (
                    <div key={item.category} className="flex items-center gap-3">
                      <span className="text-sm text-gray-600 dark:text-slate-400 w-40">{item.category}</span>
                      <div className="flex-1 rounded-full bg-white/5 h-6 relative">
                        <div
                          className={`absolute left-0 top-0 h-full ${colorClass} rounded-full transition-all duration-300`}
                          style={{ width: `${item.percentage}%` }}
                        />
                        <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs font-medium text-gray-900 dark:text-white">
                          {item.count} ({item.percentage}%)
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Additional Stats Grid */}
          {tabDataCache.profileCompleteness && tabDataCache.profileCompleteness.overall && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Field Coverage */}
              <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Feldabdeckung</h3>
                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-slate-500">
                    <span className="w-2 h-2 bg-success-500 rounded-full"></span>
                    <span>Gut</span>
                    <span className="w-2 h-2 bg-warning-500 rounded-full ml-2"></span>
                    <span>Mittel</span>
                    <span className="w-2 h-2 bg-error-500 rounded-full ml-2"></span>
                    <span>Niedrig</span>
                  </div>
                </div>
                <div className="space-y-4">
                  {(() => {
                    const fields = [
                      {
                        name: 'Profilfoto',
                        Icon: PhotoIcon,
                        value: tabDataCache.profileCompleteness.overall.with_photo,
                        color: 'emerald'
                      },
                      {
                        name: 'E-Mail-Adresse',
                        Icon: EnvelopeIcon,
                        value: tabDataCache.profileCompleteness.overall.with_email,
                        color: 'blue'
                      },
                      {
                        name: 'Telefonnummer',
                        Icon: PhoneIcon,
                        value: tabDataCache.profileCompleteness.overall.with_phone,
                        color: 'purple'
                      },
                      {
                        name: 'Geburtsdatum',
                        Icon: CakeIcon,
                        value: tabDataCache.profileCompleteness.overall.with_birthdate,
                        color: 'orange'
                      },
                      {
                        name: 'Wohnadresse',
                        Icon: MapPinIcon,
                        value: tabDataCache.profileCompleteness.overall.with_address,
                        color: 'indigo'
                      }
                    ];

                    return fields.map((field, index) => {
                      const totalMembers = tabDataCache.profileCompleteness?.overall?.total_members || 1;
                      const percentage = Math.round(field.value / totalMembers * 100);
                      const getStatusColor = (pct: number) => {
                        if (pct >= 80) return 'success';
                        if (pct >= 50) return 'warning';
                        return 'error';
                      };
                      const statusColor = getStatusColor(percentage);

                      return (
                        <div key={index} className="group hover:bg-white/5 rounded-lg p-2 -mx-2 transition-colors">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <field.Icon className="h-5 w-5 text-primary-600" />
                              <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
                                {field.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 dark:text-slate-500">
                                {field.value} von {totalMembers}
                              </span>
                              <span className={`text-sm font-bold text-${statusColor}-600 dark:text-${statusColor}-400`}>
                                {percentage}%
                              </span>
                            </div>
                          </div>
                          <div className="relative">
                            <div className="w-full h-3 rounded-full bg-white/5 overflow-hidden">
                              <div
                                className={`h-full transition-all duration-500 ease-out relative
                                  ${statusColor === 'success' ? 'bg-success-500' : ''}
                                  ${statusColor === 'warning' ? 'bg-warning-500' : ''}
                                  ${statusColor === 'error' ? 'bg-error-500' : ''}
                                `}
                                style={{ width: `${percentage}%` }}
                              >
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                <div className="mt-6 pt-4 border-t border-slate-700">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-slate-400">Durchschnittliche Vollständigkeit</span>
                    <span className="font-bold text-lg text-emerald-600 dark:text-emerald-400">
                      {Math.round(tabDataCache.profileCompleteness.overall.avg_completeness)}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Incomplete Profiles List */}
              {tabDataCache.profileCompleteness && tabDataCache.profileCompleteness.top_incomplete && tabDataCache.profileCompleteness.top_incomplete.length > 0 && (
                <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Unvollständige Profile</h3>
                  <div className="space-y-2">
                    {tabDataCache.profileCompleteness.top_incomplete.map((member, index) => (
                      <div key={index} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{member.name}</p>
                          <div className="flex gap-3 mt-1">
                            <span className={`text-xs ${member.has_photo ? 'text-success-600' : 'text-error-600'}`}>
                              {member.has_photo ? '✓' : '✗'} Foto
                            </span>
                            <span className={`text-xs ${member.has_email ? 'text-success-600' : 'text-error-600'}`}>
                              {member.has_email ? '✓' : '✗'} E-Mail
                            </span>
                            <span className={`text-xs ${member.has_phone ? 'text-success-600' : 'text-error-600'}`}>
                              {member.has_phone ? '✓' : '✗'} Tel
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-amber-600 dark:text-amber-400">
                            {member.completeness}%
                          </p>
                          <p className="text-xs text-gray-500 dark:text-slate-500">
                            {member.missing_fields} fehlend
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* No Profile Data */}
          {(!tabDataCache.profileCompleteness || !tabDataCache.profileCompleteness.overall) && (
            <div className="rounded-3xl p-8 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <ExclamationCircleIcon className="h-12 w-12 text-gray-500 dark:text-slate-500 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-slate-400">Keine Profildaten verfügbar.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'trainers' && !tabLoadingStates.trainers && (
        <div className="space-y-6">
          {/* Description */}
          <div className="rounded-2xl p-4 bg-amber-500/10 border border-amber-500/30">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Trainer-Statistik:</strong> Die erfolgreichsten Trainer und Co-Trainer des Vereins.
              Bewertet nach Anzahl der Events, Buchungen, Teilnehmern und durchschnittlicher Auslastung ihrer Veranstaltungen.
            </p>
          </div>

          {/* Top Trainers List */}
          {tabDataCache.topTrainers && tabDataCache.topTrainers.length > 0 ? (
            <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Top Trainer {selectedYear}</h3>
                <TrophyIcon className="h-5 w-5 text-warning-500" />
              </div>

              <div className="space-y-4">
                {tabDataCache.topTrainers.map((trainer, index) => (
                  <div key={index} className="p-4 rounded-xl bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {index < 3 ? (
                          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                            index === 0 ? 'bg-warning-500' : index === 1 ? 'bg-gray-400' : 'bg-warning-600'
                          } text-gray-900 dark:text-white font-bold`}>
                            {index + 1}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/5 text-gray-700 dark:text-slate-300 font-bold">
                            {index + 1}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white text-lg">
                            {trainer.trainer_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-slate-500">
                            {trainer.event_types ? trainer.event_types.split(',').slice(0, 3).join(', ') : 'Verschiedene Events'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                          {Number(trainer.total_bookings)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-500">Buchungen</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                      <div className="rounded-lg p-2 text-center bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700">
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {Number(trainer.total_events)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-500">Events</p>
                      </div>
                      <div className="rounded-lg p-2 text-center bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700">
                        <p className="text-lg font-semibold text-cyan-600 dark:text-cyan-400">
                          {Number(trainer.unique_participants)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-500">Teilnehmer</p>
                      </div>
                      <div className="rounded-lg p-2 text-center bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700">
                        <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">
                          {Number(trainer.avg_utilization).toFixed(1)}%
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-500">Ø Auslastung</p>
                      </div>
                      <div className="rounded-lg p-2 text-center bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700">
                        <p className="text-lg font-semibold text-amber-600 dark:text-amber-400">
                          {Number(trainer.success_rate).toFixed(1)}%
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-500">Erfolgsrate</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-4">
                        <span className="text-gray-600 dark:text-slate-400">
                          <CalendarIcon className="h-4 w-4 inline mr-1" />
                          {Number(trainer.unique_event_types)} Event-Typen
                        </span>
                        <span className="text-gray-600 dark:text-slate-400">
                          <UsersIcon className="h-4 w-4 inline mr-1" />
                          Ø {trainer.avg_bookings_per_event} Buchungen/Event
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30">
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  <strong>Hinweis:</strong> Die Erfolgsrate zeigt den Prozentsatz der Events mit mindestens einer Buchung.
                  Die Auslastung ist der durchschnittliche Prozentsatz der belegten Plätze über alle Events des Trainers.
                </p>
              </div>
            </div>
          ) : (
            <div className="rounded-3xl p-6 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <MegaphoneIcon className="h-12 w-12 text-gray-500 dark:text-slate-500 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-slate-400">
                Keine Trainer-Daten für {selectedYear} verfügbar
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'members' && !tabLoadingStates.members && (
        <div className="space-y-6">
          {/* Description */}
          <div className="rounded-2xl p-4 bg-blue-500/10 border border-blue-500/30">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Aktivste Mitglieder:</strong> Die 10 Mitglieder mit den meisten Buchungen im Jahr {selectedYear}.
              Zeigt Teilnahmehäufigkeit, Eventvielfalt und zeitliche Aktivität.
            </p>
          </div>

          {/* Top Members List */}
          {tabDataCache.topMembers && tabDataCache.topMembers.length > 0 ? (
            <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Top 10 Mitglieder {selectedYear}</h3>
                <UserGroupIcon className="h-5 w-5 text-info-500" />
              </div>

              <div className="space-y-4">
                {tabDataCache.topMembers.map((member, index) => (
                  <div key={index} className="p-4 rounded-xl bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {index < 3 ? (
                          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                            index === 0 ? 'bg-warning-500' : index === 1 ? 'bg-gray-400' : 'bg-warning-600'
                          } text-gray-900 dark:text-white font-bold`}>
                            {index + 1}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/5 text-gray-700 dark:text-slate-300 font-bold">
                            {index + 1}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white text-lg">
                            {member.member_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-slate-500">
                            Aktiv seit: {new Date(member.first_booking).toLocaleDateString('de-DE')}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                          {member.total_bookings}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-500">Buchungen</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                      <div className="rounded-lg p-2 text-center bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700">
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {member.unique_event_types}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-500">Event-Typen</p>
                      </div>
                      <div className="rounded-lg p-2 text-center bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700">
                        <p className="text-lg font-semibold text-cyan-600 dark:text-cyan-400">
                          {member.active_days}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-500">Aktive Tage</p>
                      </div>
                      <div className="rounded-lg p-2 text-center bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700">
                        <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">
                          {member.participation_rate}%
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-500">Teilnahmerate</p>
                      </div>
                      <div className="rounded-lg p-2 text-center bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700">
                        <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">
                          {member.avg_bookings_per_month}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-500">Ø/Monat</p>
                      </div>
                    </div>

                    <div className="border-t border-slate-700 pt-3">
                      <p className="text-xs text-gray-600 dark:text-slate-400 mb-1">
                        <strong>Bevorzugte Events:</strong>
                      </p>
                      <p className="text-xs text-gray-500 dark:text-slate-500">
                        {member.event_types ? member.event_types.split(',').slice(0, 5).join(', ') : 'Verschiedene Events'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-slate-500 mt-2">
                        <CalendarIcon className="h-3 w-3 inline mr-1" />
                        Letzte Buchung: {new Date(member.last_booking).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-3 rounded-xl bg-blue-500/10 border border-blue-500/30">
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  <strong>Hinweis:</strong> Die Teilnahmerate zeigt den Prozentsatz aller verfügbaren Events im Jahr {selectedYear},
                  an denen das Mitglied teilgenommen hat. Die Durchschnittswerte beziehen sich auf die aktiven Monate.
                </p>
              </div>
            </div>
          ) : (
            <div className="rounded-3xl p-6 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <UserGroupIcon className="h-12 w-12 text-gray-500 dark:text-slate-500 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-slate-400">
                Keine Mitgliederdaten für {selectedYear} verfügbar
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'guestcards' && !tabLoadingStates.guestcards && tabDataCache.guestCardStats && (
        <div className="space-y-6">
          {/* Description */}
          <div className="rounded-2xl p-4 bg-amber-500/10 border border-amber-500/30">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Gästekarten-Statistik:</strong> Übersicht über Verkäufe, Einnahmen und Nutzung der Gästekarten im Jahr {selectedYear}.
              Verfolgen Sie Umsätze, aktive Karten und Buchungstrends.
            </p>
          </div>

          {/* Main Statistics Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {/* Total Revenue */}
            <div className="rounded-2xl p-3 sm:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <div className="flex flex-col">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400 mb-1">Gesamtumsatz</p>
                <div className="flex items-center justify-between">
                  <p className="text-base sm:text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                    €{(tabDataCache.guestCardStats.revenue.total || 0).toFixed(2)}
                  </p>
                  <CurrencyEuroIcon className="h-5 w-5 sm:h-6 sm:w-6 text-success-500 flex-shrink-0" />
                </div>
                <p className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-500 mt-1">
                  Bezahlt: €{(tabDataCache.guestCardStats.revenue.paid || 0).toFixed(2)}
                </p>
              </div>
            </div>

            {/* Active Cards */}
            <div className="rounded-2xl p-3 sm:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <div className="flex flex-col">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400 mb-1">Aktive Karten</p>
                <div className="flex items-center justify-between">
                  <p className="text-base sm:text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                    {tabDataCache.guestCardStats.cards.active}
                  </p>
                  <TicketIcon className="h-5 w-5 sm:h-6 sm:w-6 text-info-500 flex-shrink-0" />
                </div>
                <p className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-500 mt-1">
                  von {tabDataCache.guestCardStats.cards.total} gesamt
                </p>
              </div>
            </div>

            {/* Total Bookings */}
            <div className="rounded-2xl p-3 sm:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <div className="flex flex-col">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400 mb-1">Buchungen</p>
                <div className="flex items-center justify-between">
                  <p className="text-base sm:text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                    {tabDataCache.guestCardStats.bookings.total}
                  </p>
                  <CalendarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-primary-500 flex-shrink-0" />
                </div>
                <p className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-500 mt-1">
                  {tabDataCache.guestCardStats.bookings.thisMonth} diesen Monat
                </p>
              </div>
            </div>

            {/* Sold This Month */}
            <div className="rounded-2xl p-3 sm:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <div className="flex flex-col">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400 mb-1">Verkauft (Monat)</p>
                <div className="flex items-center justify-between">
                  <p className="text-base sm:text-2xl font-bold text-amber-600 dark:text-amber-400">
                    {tabDataCache.guestCardStats.cards.soldThisMonth}
                  </p>
                  <ArrowTrendingUpIcon className="h-5 w-5 sm:h-6 sm:w-6 text-warning-500 flex-shrink-0" />
                </div>
                <p className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-500 mt-1">
                  Letzter: {tabDataCache.guestCardStats.cards.soldLastMonth}
                </p>
              </div>
            </div>

            {/* Unpaid Revenue */}
            <div className="rounded-2xl p-3 sm:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <div className="flex flex-col">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400 mb-1">Offen</p>
                <div className="flex items-center justify-between">
                  <p className="text-base sm:text-2xl font-bold text-rose-600 dark:text-rose-400">
                    €{(tabDataCache.guestCardStats.revenue.unpaid || 0).toFixed(2)}
                  </p>
                  <ExclamationCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-error-500 flex-shrink-0" />
                </div>
                <p className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-500 mt-1">
                  Unbezahlt
                </p>
              </div>
            </div>
          </div>

          {/* Card Type Distribution */}
          {tabDataCache.guestCardStats.typeDistribution && tabDataCache.guestCardStats.typeDistribution.length > 0 && (
            <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Kartentyp-Verteilung</h3>
                <ChartBarIcon className="h-5 w-5 text-amber-500" />
              </div>
              <div className="space-y-3">
                {tabDataCache.guestCardStats.typeDistribution.map((type, index) => (
                  <div key={index} className="p-3 rounded-xl bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900 dark:text-white">{type.name}</span>
                      <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                        €{(type.revenue || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <span className="text-gray-500 dark:text-slate-500">Verkauft:</span>
                        <span className="ml-1 font-medium text-gray-700 dark:text-slate-300">{type.count}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-slate-500">Ø Rest:</span>
                        <span className="ml-1 font-medium text-gray-700 dark:text-slate-300">{type.avgRemaining}%</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-slate-500">Ø Preis:</span>
                        <span className="ml-1 font-medium text-gray-700 dark:text-slate-300">
                          €{type.count > 0 ? ((type.revenue || 0) / type.count).toFixed(2) : '0.00'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Monthly Revenue Chart */}
          {tabDataCache.guestCardStats.monthlyRevenue && tabDataCache.guestCardStats.monthlyRevenue.length > 0 && (
            <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Monatliche Einnahmen</h3>
                <ArrowTrendingUpIcon className="h-5 w-5 text-amber-500" />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 px-4 text-sm font-medium text-gray-700 dark:text-slate-300">Monat</th>
                      <th className="text-right py-2 px-4 text-sm font-medium text-gray-700 dark:text-slate-300">Umsatz</th>
                      <th className="text-right py-2 px-4 text-sm font-medium text-gray-700 dark:text-slate-300">Verkauft</th>
                      <th className="text-right py-2 px-4 text-sm font-medium text-gray-700 dark:text-slate-300">Käufer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tabDataCache.guestCardStats.monthlyRevenue.map((month, index) => (
                      <tr key={index} className="border-b border-slate-700/50">
                        <td className="py-2 px-4 text-sm text-gray-900 dark:text-white">
                          {new Date(month.month + '-01').toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
                        </td>
                        <td className="py-2 px-4 text-sm text-right">
                          <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                            €{(month.revenue || 0).toFixed(2)}
                          </span>
                        </td>
                        <td className="py-2 px-4 text-sm text-right text-gray-900 dark:text-white">
                          {month.cardsSold}
                        </td>
                        <td className="py-2 px-4 text-sm text-right text-gray-900 dark:text-white">
                          {month.uniqueBuyers}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-2xl p-4" >
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Durchschnittswerte</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-xs text-gray-600 dark:text-slate-400">Einträge pro Karte:</span>
                  <span className="text-xs font-medium text-gray-900 dark:text-white">
                    {Number(tabDataCache.guestCardStats.averages.entriesPerCard || 0).toFixed(1)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-gray-600 dark:text-slate-400">Preis pro Karte:</span>
                  <span className="text-xs font-medium text-gray-900 dark:text-white">
                    €{Number(tabDataCache.guestCardStats.averages.pricePerCard || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-2xl p-4" >
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Kartennutzung</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-xs text-gray-600 dark:text-slate-400">Aufgebraucht:</span>
                  <span className="text-xs font-medium text-gray-900 dark:text-white">
                    {tabDataCache.guestCardStats.cards.depleted}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-gray-600 dark:text-slate-400">Events gebucht:</span>
                  <span className="text-xs font-medium text-gray-900 dark:text-white">
                    {tabDataCache.guestCardStats.bookings.uniqueEvents}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default MemberStatistics;